import { booleanAttribute, Component, effect, inject, input, signal } from '@angular/core';
import { email, form, maxLength, pattern, required } from '@angular/forms/signals';

import type { ContactFormSubmission } from '../../types/contact-form-submission';
import type { FormType } from '../../types/form-type';

import { Environment } from '../../../environments/environment';
import { FilteringService } from '../../services/filtering';
import { ListingsService } from '../../services/listings/listings';
import { SeoService } from '../../services/seo/seo';
import { StateService } from '../../services/state/state';
import { FormField } from '../form-field/form-field';
import { LottiPlayer } from '../lotti-player/lotti-player';

@Component({
  imports: [FormField, LottiPlayer],
  selector: 'app-contact-form',
  styleUrl: './contact-form.css',
  templateUrl: './contact-form.html',
})
export class ContactForm {
  private readonly _filteringService = inject(FilteringService);
  private readonly _listingsService = inject(ListingsService);
  private readonly _seoService = inject(SeoService);
  private readonly _stateService = inject(StateService);

  public readonly formType = input.required<FormType>();
  public readonly showComment = input(true, { transform: booleanAttribute });
  public readonly showHeader = input(true, { transform: booleanAttribute });

  private readonly _contactFormModel = signal<ContactFormSubmission>({
    comment: '',
    email: '',
    firstName: '',
    formDisplayName: '',
    formTitle: '',
    lastName: '',
    phone: '',
  });
  protected readonly contactForm = form(this._contactFormModel, (schemaPath) => {
    required(schemaPath.firstName);
    required(schemaPath.lastName);
    required(schemaPath.email);
    email(schemaPath.email);
    required(schemaPath.phone);
    pattern(schemaPath.phone, /^\+?1?\s?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/);
    maxLength(schemaPath.comment, 120);
  });
  protected readonly formSubmitted = signal(false);
  protected readonly successCompleted = signal(false);
  protected readonly successLottiSource = signal('/Success.json');

  private readonly _displayName = signal('');
  private _submitting = false;
  private readonly _title = signal('');

  constructor() {
    // Populate form display name and title based on form type and selected listing (if applicable).
    effect(() => {
      switch (this.formType()) {
        case 'emptyRepListings':
          this._displayName.set(
            `Unavailable Rep Listings - ${this._stateService.communityDisplayName()}`,
          );
          this._title.set('emptyRepListings');
          break;
        case 'propertyPreview':
          this._displayName.set(`Property Preview - ${this._stateService.communityDisplayName()}`);
          this._title.set('propertyPreview');
          break;
        case 'propertyShare':
          this._displayName.set(`Property Share - ${this._stateService.communityDisplayName()}`);
          this._title.set('propertyShare');
          break;
        case 'unavailableModels':
          this._displayName.set(
            `Unavailable Models - ${this._stateService.communityDisplayName()}`,
          );
          this._title.set('unavailableModels');
          break;
        case 'generalInquiry':
        default:
          this._displayName.set(
            `General Homefinder Inquiry - ${this._stateService.communityDisplayName()}`,
          );
          this._title.set('hfGeneralInquiry');
          break;
      }

      this._contactFormModel.update((f) => {
        f.formDisplayName = this._displayName();
        f.formTitle = this._title();

        if (this._listingsService.selectedListing()) {
          f.address = this._listingsService.selectedListing()?.address.streetAddress;
          f.uli = this._listingsService.selectedListing()?.uliKey;
          f.vls = this._listingsService.selectedListing()?.vls;
        }

        if (this.formType() === 'emptyRepListings' && this._filteringService.reps()) {
          f.repId = this._filteringService.reps();
        } else if (
          this.formType() === 'unavailableModels' &&
          this._filteringService.models().length
        ) {
          f.model = this._filteringService.models()[0];
        }

        return f;
      });

      if (Environment.production) {
        this.successLottiSource.set(`/homefinder/success-${this._stateService.community()}.json`);
      }

      this._seoService.dataLayerPush('form_submit', {
        formName: this._displayName(),
      });
    });

    // Send the form entered event when the form is interacted with for the first time.
    effect(() => {
      if (this.formSubmitted()) {
        return;
      }

      if (this.contactForm().dirty()) {
        this._seoService.dataLayerPush('form_start', {
          formName: this._displayName(),
        });
      }
    });
  }

  protected reset(submitted = false): void {
    this._contactFormModel.set({
      address: undefined,
      comment: '',
      email: '',
      firstName: '',
      formDisplayName: this._displayName(),
      formTitle: this._title(),
      lastName: '',
      model: undefined,
      phone: '',
      repId: undefined,
      uli: undefined,
      url: undefined,
      vls: undefined,
    });

    if (this._listingsService.selectedListing()) {
      this._contactFormModel.update((f) => {
        f.address = this._listingsService.selectedListing()?.address.streetAddress;
        f.uli = this._listingsService.selectedListing()?.uliKey;
        f.vls = this._listingsService.selectedListing()?.vls;

        return f;
      });
    }
    this.contactForm().reset();

    this.formSubmitted.set(submitted);

    this.successCompleted.set(false);
  }

  protected async submit(): Promise<void> {
    if (!this.contactForm().valid()) {
      return;
    }
    if (this._submitting) {
      return;
    }

    this._submitting = true;

    // Submit the form data to the API.
    const _result = await fetch(`https://api.v2.thevillages.com/forms/${this._title()}`, {
      body: JSON.stringify(this._contactFormModel()),
      headers: {
        Authorization: Environment.basicToken,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- This is the correct header name for content type.
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });

    if (!_result.ok) {
      // Display an error toast message if the submission fails.
      this._stateService.toastMessage.set({
        duration: 4000,
        icon: 'error',
        message: 'There was an error submitting the form. Please try again later.',
        title: 'Submission Failed',
      });

      this._submitting = false;
      return;
    }

    // Reset the form after submission.
    this.reset(true);
    this._submitting = false;
  }
}
